import { PencilSimpleLine, Plus } from "@phosphor-icons/react";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { CARD_ICONS, PAYMENT_METHODS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { PaymentSource } from "common/types";

interface PrimaryPaymentSourceProps {
  paymentSource: PaymentSource;
  onEdit: () => void;
  isLoading: boolean;
}

export default function PrimaryPaymentSource({
  paymentSource,
  onEdit,
  isLoading,
}: PrimaryPaymentSourceProps) {
  const workspaceContext = useSelectedWorkspaceContext();

  const canUpdatePaymentMethod =
    !workspaceContext || workspaceContext.role === "owner";

  let Icon = null;
  if (paymentSource) {
    Icon =
      paymentSource.type === "card"
        ? CARD_ICONS[paymentSource.card.brand]
        : PAYMENT_METHODS[paymentSource.type].icon;
  }

  if (isLoading)
    return (
      <div className="flex items-center gap-x-2 rounded-3xl border border-black-200 bg-black-100 px-4 py-2">
        <Skeleton className="h-6 w-10" />
        <Skeleton className="h-[19px] w-36" />
        <Skeleton className="ml-auto size-8 rounded-full" />
      </div>
    );

  if (paymentSource)
    return (
      <div className="flex items-center gap-x-2 rounded-3xl bg-purple-50 px-4 py-2 ring ring-purple-200">
        <Icon />
        <span className="flex-1 text-nowrap text-left text-sm-bold">
          {paymentSource.type === "card"
            ? `Card ending with ${paymentSource.card.last4}`
            : paymentSource.paypal.email}
        </span>
        <Button variant="outline-black" intent="iconOnly" onClick={onEdit}>
          <PencilSimpleLine />
        </Button>
      </div>
    );

  const addPaymentMethodButton = (
    <button
      type="button"
      className="group flex items-center justify-start gap-2 rounded-3xl border border-dashed border-black-300 bg-white px-4 py-2 transition-colors duration-300 ease-in-out hover:bg-whiteGray disabled:cursor-not-allowed disabled:bg-black-100"
      disabled={!canUpdatePaymentMethod}
      onClick={onEdit}
    >
      <div className="rounded-full bg-black-900 p-2 group-disabled:bg-black-200">
        <Plus
          color="white"
          weight="bold"
          size={16}
          className="group-disabled:fill-black-950"
        />
      </div>

      <span className="text-sm-bold group-disabled:text-black-400">
        Add payment method
      </span>
    </button>
  );

  return canUpdatePaymentMethod ? (
    addPaymentMethodButton
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>{addPaymentMethodButton}</TooltipTrigger>
      <TooltipContent>
        Only the Owner can add a new payment method
      </TooltipContent>
    </Tooltip>
  );
}
